import { useState } from "react";
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
// @mui
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
// hooks
import useAuth from "../../hooks/useAuth";
import useResponsive from "../../hooks/useResponsive";
// utils
import axios from '../../utils/axios';
// routes
import { PATH_SCOUT } from '../../routes/paths';

// ----------------------------------------------------------------------

export default function ScoutCrewCard({
  profile,
  selectProfile = null,
  proPlan=false,
  reload=null,
}) {
  const { user } = useAuth();
  const smUp = useResponsive('up', 'sm');
  const xlUp = useResponsive('up', 'xl');
  const lgUp = useResponsive('up', 'lg');
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { push } = router;

  const stopPropagationForTab = (e) => {
    e.stopPropagation();
  };

  const acceptCrew = async () => {
    try {
      setLoading(true);
      const companyId = user?.employee.company?.[0]?.id;
      if (!profile?.id || !companyId) {
        enqueueSnackbar("요청이 실패하였습니다. 관리자에 문의해주세요.", { variant: 'warning' });
        return ;
      }
      const response = await axios.post(`/api/company/${companyId}/scout/${profile?.id}/accept/`);
      enqueueSnackbar(`${profile?.name} 크루를 인재풀에 추가하였습니다.`, { variant: 'success' });

      setLoading(false);
      const nextCrewId = response.data.next_crew;
      if (!nextCrewId) {
        enqueueSnackbar("추천받은 크루를 모두 확인했습니다.");
        setTimeout(() =>  push({pathname: PATH_SCOUT.root}), 1000);
      } else {
        reload();
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 400 && error.response.data && error.response.data.message) {
        enqueueSnackbar(error.response.data.message, { variant: 'warning' });
      } else {
        enqueueSnackbar("요청이 실패하였습니다. 관리자에 문의해주세요.", { variant: 'warning' });
      }
      setLoading(false);
    }
  };

  const declineCrew = async () => {
    try {
      setLoading(true);
      const companyId = user?.employee.company?.[0]?.id;
      if (!profile?.id || !companyId) {
        enqueueSnackbar("요청이 실패하였습니다. 관리자에 문의해주세요.", { variant: 'warning' });
        return ;
      }
      const response = await axios.post(`/api/company/${companyId}/scout/${profile?.id}/decline/`, { reason });
      enqueueSnackbar(`${profile?.name} 크루에 대한 피드백이 제출되었습니다.`, { variant: 'info' });

      setLoading(false);
      setOpen(false);
      const nextCrewId = response.data.next_crew;
      if (!nextCrewId) {
        enqueueSnackbar("추천받은 크루를 모두 확인했습니다.");
        setTimeout(() =>  push({pathname: PATH_SCOUT.root}), 1000);
      } else {
        reload();
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.status === 400 && error.response.data && error.response.data.message) {
        enqueueSnackbar(error.response.data.message, { variant: 'warning' });
      } else {
        enqueueSnackbar("요청이 실패하였습니다. 관리자에 문의해주세요.", { variant: 'warning' });
      }
      setLoading(false);
    }
  };

  return (
    <Card
      sx={{
        ...(selectProfile && {
          '&:hover': {
            cursor: 'pointer',
            boxShadow: (theme) => theme.customShadows.z24,
          },
        }),
      }}
      onClick={() => selectProfile && selectProfile(profile)}
    >
      <CardContent>
        <Stack direction={"row"} spacing={1} sx={{ width: 1, mb: 2 }} justifyContent={"space-between"}>
          <Stack
            direction={"row"} spacing={1}
            sx={{ overflow: "hidden" }}
          >
            <Avatar
              src={`https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crewji/crewji_${profile?.crew_hash || '5'}.png`}
              alt={profile?.name}
              color={'default'}
              sx={{
                margin: 'auto',
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: 'common.white',
                width: 40,
                height: 40,
                minWidth: 40,
                minHeight: 40,
              }}
            />
            <Stack
              sx={{ width: 1, overflow: "hidden" }}
            >
              <Typography variant="subtitle2" sx={{ textOverflow: "ellipsis" }} noWrap>
                {profile?.name} 크루
              </Typography>
              <Typography variant="caption" sx={{ textOverflow: "ellipsis", color: "text.secondary" }} noWrap>
                {profile?.main_role?.card_display_name || profile?.main_role?.role}
              </Typography>
            </Stack>
          </Stack>
          {proPlan && (
            <IconButton
              edge="end"
              onClick={() => selectProfile && selectProfile(profile)}
              color="primary"
              sx={{ bgcolor: "grey.200", minWidth: 40 }}
              size="small"
            >
              <SvgIconStyle src={"/icons/icon-arrow-right-android-mono.svg"} sx={{ width: 18, height: 18 }} />
            </IconButton>
          )}
        </Stack>
        <Stack
          spacing={1}
          sx={{
            width: 1, mb: proPlan ? 3 : 1, overflow: "hidden",
            ...smUp && { minHeight: 124, maxHeight: 124 }
          }}
        >
          {profile?.scout_reason?.length > 0 && profile?.scout_reason?.split('\n').filter(m => m).map((reason, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
              <SvgIconStyle src={"/icons/icon-check-mono.svg"} sx={{ minWidth: 16, minHeight: 16, maxWidth: 16, maxHeight: 16, my: 0.125 }} />
              <Typography key={index} variant="caption" sx={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', }}>
                {reason}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {proPlan ? (
          <Stack direction="row" spacing={2}>
            <LoadingButton
              fullWidth
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" sx={{ width: 20, height: 20, mr: -0.5 }} />}
              disableRipple
              onClick={(e) => { e.stopPropagation(); acceptCrew() }}
              loading={loading}
            >
              {(!lgUp || xlUp) && "인재풀에"} 추가
            </LoadingButton>
            <LoadingButton
              fullWidth
              variant="outlined"
              color="inherit"
              sx={{
                color: "text.secondary", borderColor: "text.secondary"
              }}
              disableRipple
              startIcon={<Iconify icon="eva:close-fill" sx={{ width: 20, height: 20, mr: -0.5 }} />}
              loading={loading}
              onClick={(e) => { e.stopPropagation(); setOpen(true) }}
            >
              추가 안함
            </LoadingButton>
            <Dialog
              open={open}
              fullWidth
              maxWidth="dg"
              onClick={stopPropagationForTab}
            >
              <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
                <Typography variant="h5" gutterBottom>
                  이 크루를 인재풀에 추가하지 않는 이유를 알려주세요.
                </Typography>
                <Typography variant="body2" sx={{ mb: 3 }}>
                  다음 추천에 반영하겠습니다.<br/>인재풀에서 제외된 크루 내역은 스카우트 첫 화면에서 확인하실 수 있습니다.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </DialogContent>
              <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
                <LoadingButton
                  size="large"
                  sx={{ px: 4 }}
                  onClick={() => setOpen(false)}
                  loading={loading}
                >
                  취소
                </LoadingButton>
                <LoadingButton
                  variant="contained"
                  size="large"
                  sx={{ px: 4 }}
                  onClick={() => declineCrew()}
                  loading={loading}
                  disabled={!reason}
                >
                  제출하기
                </LoadingButton>
              </DialogActions>
            </Dialog>
          </Stack>
        ):(
        <Box
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <IconButton
            onClick={() => selectProfile && selectProfile(profile)}
            color="primary"
            sx={{ bgcolor: "grey.200" }}
            size="small"
          >
            <SvgIconStyle src={"/icons/icon-arrow-right-android-mono.svg"} sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>
        )}
      </CardContent>
    </Card>
  )
}
